import React from 'react';
import { Drawer } from '~/shared/components/Drawer';
import { useTranslation } from '~/shared/utils/translation';
import { useScrollLock } from '~/shared/hooks';
import Image from 'next/image';
import Checkmark from '~/icons/checkmark.svg';
import Close from '~/icons/close.svg';
import styles from './ImercoPlusDrawer.module.scss';
import Typography from '~/shared/components/Typography/Typography';

type ImercoPlusDrawerProps = {
    open: boolean;
    onClose: () => void;
};

// Dummy app IDs - replace with real ones later
const APP_STORE_ID = '1234567890'; // Replace with actual App Store ID
const GOOGLE_PLAY_PACKAGE = 'com.imerco.plus'; // Replace with actual package name

// App Store URLs
const APP_STORE_URL = `https://apps.apple.com/app/id${APP_STORE_ID}`;
const APP_STORE_DEEP_LINK = `itms-apps://itunes.apple.com/app/id${APP_STORE_ID}`;

// Google Play URLs
const GOOGLE_PLAY_URL = `https://play.google.com/store/apps/details?id=${GOOGLE_PLAY_PACKAGE}`;
const GOOGLE_PLAY_DEEP_LINK = `market://details?id=${GOOGLE_PLAY_PACKAGE}`;

// Device detection
const isIOS = () => {
    if (typeof window === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

const isAndroid = () => {
    if (typeof window === 'undefined') return false;
    return /Android/.test(navigator.userAgent);
};

const handleAppStoreClick = () => {
    if (isIOS()) {
        // Try deep link first, fallback to web URL
        window.location.href = APP_STORE_DEEP_LINK;
        setTimeout(() => {
            window.location.href = APP_STORE_URL;
        }, 500);
    } else {
        window.open(APP_STORE_URL, '_blank');
    }
};

const handleGooglePlayClick = () => {
    if (isAndroid()) {
        // Try deep link first, fallback to web URL
        window.location.href = GOOGLE_PLAY_DEEP_LINK;
        setTimeout(() => {
            window.location.href = GOOGLE_PLAY_URL;
        }, 500);
    } else {
        window.open(GOOGLE_PLAY_URL, '_blank');
    }
};

const ImercoPlusDrawer: React.FC<ImercoPlusDrawerProps> = ({ open, onClose }) => {
    const { translate } = useTranslation();
    const { lock, unlock } = useScrollLock();

    React.useEffect(() => {
        if (open) {
            lock();
        }
        return () => unlock();
    }, [lock, unlock, open]);

    return (
        <Drawer open={open} onClose={onClose} isInfo>
            <div className={styles.content}>
                <div className={styles.header}>
                    <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                        <Close />
                    </button>
                </div>

                <div className={styles.container}>
                    <div className={styles.containerInner}>
                        {/* Logo Section */}
                        <div className={styles.logoSection}>
                            <Image
                                src="/assets/imerco-plus-logo-full.svg"
                                alt="IMERCO PLUS"
                                width={200}
                                height={60}
                                className={styles.logo}
                            />
                            <Typography
                                as="p"
                                variant="paragraph-M"
                                weight="medium"
                                className={styles.tagline}
                            >
                                {translate('loyalty.imercoPlus.drawer.tagline')}
                            </Typography>
                        </div>

                        {/* Benefits Section */}
                        <ul className={styles.benefitsList}>
                            <li className={styles.benefitItem}>
                                <Checkmark className={styles.checkmark} />
                                <Typography as="span" variant="paragraph-S">
                                    {translate('loyalty.imercoPlus.drawer.benefit1')}
                                </Typography>
                            </li>
                            <li className={styles.benefitItem}>
                                <Checkmark className={styles.checkmark} />
                                <Typography as="span" variant="paragraph-S">
                                    {translate('loyalty.imercoPlus.drawer.benefit2')}
                                </Typography>
                            </li>
                            <li className={styles.benefitItem}>
                                <Checkmark className={styles.checkmark} />
                                <Typography as="span" variant="paragraph-S">
                                    {translate('loyalty.imercoPlus.drawer.benefit3')}
                                </Typography>
                            </li>
                        </ul>

                        {/* QR Codes Section */}
                        <div className={styles.qrSection}>
                            <a
                                href={APP_STORE_URL}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleAppStoreClick();
                                }}
                                className={styles.qrLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <div className={styles.qrColumn}>
                                    <Image
                                        src="/assets/download-on-appstore.png"
                                        alt="Download on the App Store"
                                        width={150}
                                        height={150}
                                        className={styles.downloadImage}
                                    />
                                    <Image
                                        src="/assets/imerco-plus-download-qr-appstore.png"
                                        alt="App Store QR Code"
                                        width={150}
                                        height={150}
                                        className={styles.qrCode}
                                    />
                                </div>
                            </a>
                            <a
                                href={GOOGLE_PLAY_URL}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleGooglePlayClick();
                                }}
                                className={styles.qrLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <div className={styles.qrColumn}>
                                    <Image
                                        src="/assets/download-on-googleplay.png"
                                        alt="GET IT ON Google Play"
                                        width={150}
                                        height={150}
                                        className={styles.downloadImage}
                                    />
                                    <Image
                                        src="/assets/imerco-plus-download-qr-googleplay.png"
                                        alt="Google Play QR Code"
                                        width={150}
                                        height={150}
                                        className={styles.qrCode}
                                    />
                                </div>
                            </a>
                        </div>

                        <div className={styles.downloadSection}>
                            <a
                                href={APP_STORE_URL}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleAppStoreClick();
                                }}
                                className={styles.downloadLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Image
                                    src="/assets/download-on-appstore.png"
                                    alt="Download on the App Store"
                                    width={150}
                                    height={150}
                                    className={styles.downloadImage}
                                />
                                <Image
                                    src="/assets/logo-imerco-plus-alt.png"
                                    alt="IMERCO PLUS"
                                    width={62}
                                    height={62}
                                    className={styles.logoImage}
                                />
                            </a>
                            <a
                                href={GOOGLE_PLAY_URL}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleGooglePlayClick();
                                }}
                                className={styles.downloadLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Image
                                    src="/assets/download-on-googleplay.png"
                                    alt="GET IT ON Google Play"
                                    width={150}
                                    height={150}
                                    className={styles.downloadImage}
                                />
                                <Image
                                    src="/assets/logo-imerco-plus-alt.png"
                                    alt="IMERCO PLUS"
                                    width={62}
                                    height={62}
                                    className={styles.logoImage}
                                />
                            </a>
                        </div>

                        {/* Phone Image Section */}
                        <div className={styles.phoneSection}>
                            <Image
                                src="/assets/imerco-plus-teaser.webp"
                                alt="IMERCO PLUS App"
                                width={300}
                                height={600}
                                className={styles.phoneImage}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default ImercoPlusDrawer;
